import React from 'react';
import { motion } from 'motion/react';
import { Button } from '../ui/button';
import { ArrowRight, CheckCircle, Zap, Users, TrendingUp, Sparkles, MessageCircle } from 'lucide-react';
import { useWhatsAppDemo } from '../../hooks/useWhatsAppDemo';
import { fadeInUp, staggerContainer, staggerItem } from '../../lib/motion';

interface Benefit {
  icon: React.ComponentType<{ className?: string }>;
  text: string;
}

const FinalCTA: React.FC = () => {
  const benefits: Benefit[] = [
    {
      icon: Zap,
      text: 'Setup in 5 minuti'
    },
    {
      icon: Users,
      text: 'Assistenza dedicata'
    },
    {
      icon: CheckCircle,
      text: 'Garanzia 30 giorni'
    },
    {
      icon: TrendingUp,
      text: 'ROI garantito'
    }
  ];

  const handleDemoRequest = useWhatsAppDemo('Ciao, vorrei richiedere una demo personalizzata di ChatLy');

  const handleContactUs = () => {
    window.open('mailto:info@chatly.it?subject=Richiesta informazioni ChatLy&body=Ciao, vorrei maggiori informazioni su ChatLy per il mio business.', '_blank');
  };

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-primary via-primary-dark to-secondary relative overflow-hidden">
      <div className="absolute inset-0 opacity-10" aria-hidden="true">
        <div className="absolute top-10 left-10 w-24 h-24 border-2 border-white rounded-full"></div>
        <div className="absolute bottom-20 left-1/4 w-32 h-32 border-2 border-white rounded-full"></div>
        <div className="absolute top-1/3 right-1/4 w-6 h-6 bg-white rounded-full"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          {...fadeInUp}
          className="text-center"
        >
          <div className="max-w-4xl mx-auto">
            <motion.div
              {...fadeInUp}
              transition={{ delay: 0.1 }}
              className="mb-8"
            >
              <h2 className="text-4xl lg:text-6xl font-heading font-bold text-white mb-6 leading-tight">
                Pronto a Rivoluzionare il tuo{' '}
                <span className="text-white">
                  Customer Service?
                </span>
              </h2>
              <p className="text-xl lg:text-2xl text-white/90 leading-relaxed max-w-3xl mx-auto">
                Richiedi una demo personalizzata e scopri come ChatLy può trasformare il tuo business in meno di una settimana
              </p>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
            >
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  variants={staggerItem}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 text-center border border-white/20"
                >
                  <benefit.icon className="h-8 w-8 text-white mx-auto mb-3" aria-hidden="true" />
                  <p className="text-white font-medium text-sm lg:text-base">
                    {benefit.text}
                  </p>
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              {...fadeInUp}
              transition={{ delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-6 justify-center items-center"
            >
              <motion.div
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <Button
                  onClick={handleDemoRequest}
                  size="lg"
                  className="bg-white text-primary hover:bg-gray-100 font-bold text-lg px-10 py-6 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 group"
                >
                  <Sparkles className="mr-2 h-5 w-5" aria-hidden="true" />
                  Richiedi Demo Personalizzata
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" aria-hidden="true" />
                </Button>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <Button
                  onClick={handleContactUs}
                  variant="outline"
                  size="lg"
                  className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary font-bold text-lg px-10 py-6 rounded-full transition-all duration-300"
                >
                  <MessageCircle className="mr-2 h-5 w-5" aria-hidden="true" />
                  Contattaci
                </Button>
              </motion.div>
            </motion.div>

            <motion.div
              {...fadeInUp}
              transition={{ delay: 0.3 }}
              className="mt-12 bg-white/10 backdrop-blur-sm rounded-2xl p-6 lg:p-8 border border-white/20"
            >
              <div className="flex items-center justify-center gap-3 mb-4">
                <CheckCircle className="h-6 w-6 text-white" aria-hidden="true" />
                <h3 className="text-xl font-bold text-white">
                  Garanzia "Soddisfatti o Rimborsati"
                </h3>
              </div>
              <p className="text-white/90 text-center max-w-2xl mx-auto">
                Prova ChatLy senza rischi per 30 giorni. Se non vedi risultati concreti nel tuo business,
                ti rimborsiamo ogni centesimo. Nessuna domanda, nessun problema.
              </p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FinalCTA;
